import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronLeft, ChevronRight, Copy, Check, Sparkles, 
    ArrowRight, Clock, Tag 
} from 'lucide-react';

const SpecialOffers = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [copiedId, setCopiedId] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(2);
    const navigate = useNavigate();
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/api/v1';
    const storageBase = apiBase.replace(/\/api\/v1\/?$/, '') + '/storage';

    // Handle responsive items per page
    useEffect(() => {
        const updateItemsPerPage = () => {
            if (window.innerWidth >= 992) {
                setItemsPerPage(2);
            } else {
                setItemsPerPage(1);
            }
        };

        updateItemsPerPage();
        window.addEventListener('resize', updateItemsPerPage);
        return () => window.removeEventListener('resize', updateItemsPerPage);
    }, []);

    // Fetch all active coupons & offers
    useEffect(() => {
        const fetchOffers = async () => {
            try {
                const cleanApiBase = apiBase.replace(/\/+$/, '');
                const response = await fetch(`${cleanApiBase}/coupons?all=true`);
                const data = await response.json();
                
                if (data.status) {
                    const list = Array.isArray(data.data) ? data.data : (data.data?.data || []);
                    setOffers(list);
                } else {
                    const fallbackResp = await fetch(`${cleanApiBase}/coupons/featured`);
                    const fallbackData = await fallbackResp.json();
                    if (fallbackData.status && Array.isArray(fallbackData.data)) {
                        setOffers(fallbackData.data);
                    }
                }
            } catch (err) {
                console.error("Error fetching special offers:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchOffers();
    }, [apiBase]);

    const maxIndex = Math.max(0, offers.length - itemsPerPage);

    const handlePrev = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
    }, [maxIndex]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
    }, [maxIndex]);

    // Autoplay slider (every 5 seconds when not hovered and has multiple slides)
    useEffect(() => {
        if (offers.length <= itemsPerPage || isPaused) return;

        const interval = setInterval(() => {
            handleNext();
        }, 5000);

        return () => clearInterval(interval);
    }, [offers.length, itemsPerPage, isPaused, handleNext]);

    // Handle touch swipe for mobile
    const handleTouchStart = (e) => {
        touchStartX.current = e.targetTouches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.targetTouches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (!touchStartX.current || !touchEndX.current) return;
        const diffX = touchStartX.current - touchEndX.current;
        if (diffX > 45) {
            handleNext();
        } else if (diffX < -45) {
            handlePrev();
        }
        touchStartX.current = 0;
        touchEndX.current = 0;
    };

    const handleCopyCode = (e, code, id) => {
        e.stopPropagation();
        if (!code) return;
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2500);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const options = { month: 'short', day: 'numeric', year: 'numeric' };
            return new Date(dateStr).toLocaleDateString('en-US', options);
        } catch {
            return dateStr;
        }
    };

    const stripHtml = (html) => {
        if (!html) return '';
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent || "";
        } catch {
            return html.replace(/<[^>]*>?/gm, '');
        }
    };

    if (loading || offers.length === 0) return null;

    const totalSlides = maxIndex + 1;

    return (
        <section className="py-3 py-md-5 bg-light position-relative overflow-hidden">
            <div className="container">

                {/* ── Desktop Header ────────────────────────────────────────── */}
                <div className="d-none d-md-flex justify-content-between align-items-end mb-4">
                    <div>
                        <div className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill bg-primary bg-opacity-10 text-primary small fw-bold mb-2">
                            <Sparkles size={14} className="text-primary" />
                            <span>EXCLUSIVE DISCOUNTS</span>
                        </div>
                        <h2 className="fw-black text-dark mb-1 fs-2" style={{ letterSpacing: '-0.5px' }}>
                            Special Offers & Coupons
                        </h2>
                        <p className="text-muted mb-0 small">
                            Unlock limited-time discounts, promo codes, and special stays with ZoyoTrip
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                        <button 
                            onClick={() => navigate('/offers')}
                            className="btn btn-link text-decoration-none text-primary fw-bold p-0 d-inline-flex align-items-center gap-1 hover-arrow"
                        >
                            <span>View All Offers</span>
                            <ArrowRight size={16} className="transition-arrow" />
                        </button>

                        {offers.length > itemsPerPage && (
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    onClick={handlePrev}
                                    className="btn btn-white shadow-sm border rounded-circle p-2 d-flex align-items-center justify-content-center carousel-nav-btn"
                                    aria-label="Previous offers"
                                    title="Previous"
                                    style={{ width: '38px', height: '38px' }}
                                >
                                    <ChevronLeft size={18} className="text-dark" />
                                </button>
                                <button
                                    onClick={handleNext}
                                    className="btn btn-white shadow-sm border rounded-circle p-2 d-flex align-items-center justify-content-center carousel-nav-btn"
                                    aria-label="Next offers"
                                    title="Next"
                                    style={{ width: '38px', height: '38px' }}
                                >
                                    <ChevronRight size={18} className="text-dark" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Mobile Header (Compact & High Contrast) ────────────────── */}
                <div className="d-flex d-md-none justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="fw-black text-dark mb-0 fs-5" style={{ letterSpacing: '-0.3px' }}>
                            Offers & Coupons
                        </h3>
                        <small className="text-muted" style={{ fontSize: '12px' }}>
                            Save big on your next stay
                        </small>
                    </div>
                    <button 
                        onClick={() => navigate('/offers')}
                        className="btn btn-sm btn-link text-primary fw-bold text-decoration-none p-0 d-inline-flex align-items-center gap-1"
                        style={{ fontSize: '13px' }}
                    >
                        <span>View All</span>
                        <ArrowRight size={14} />
                    </button>
                </div>

                {/* ── Carousel Viewport Container ───────────────────────────── */}
                <div 
                    className="carousel-viewport position-relative overflow-hidden"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div 
                        className="d-flex carousel-track"
                        style={{
                            transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)`,
                            transition: 'transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)',
                        }}
                    >
                        {offers.map((offer) => {
                            const isPercentage = offer.discount_type === 'percentage';
                            const discountDisplay = offer.discount_value 
                                ? (isPercentage ? `${parseFloat(offer.discount_value)}% OFF` : `₹${parseFloat(offer.discount_value)} OFF`)
                                : null;
                            const isSingleOffer = offers.length === 1;
                            const offerImageUrl = offer.image 
                                ? `${storageBase}/${offer.image}` 
                                : 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1000&q=80';

                            return (
                                <div 
                                    key={offer.id}
                                    className="carousel-slide px-1 px-md-2"
                                    style={{
                                        flex: isSingleOffer ? '0 0 100%' : `0 0 ${100 / itemsPerPage}%`,
                                        maxWidth: isSingleOffer ? '100%' : `${100 / itemsPerPage}%`,
                                    }}
                                >
                                    {/* ── CARD: Clean Separation of Banner Artwork & Content ── */}
                                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white offer-card-clean">
                                        
                                        {/* Top Banner Image Container */}
                                        <div 
                                            className="position-relative cursor-pointer overflow-hidden banner-img-wrap"
                                            onClick={() => navigate(`/offers/${offer.id}`)}
                                            style={{ 
                                                height: window.innerWidth < 768 ? '175px' : (isSingleOffer ? '220px' : '190px'),
                                                backgroundColor: '#f1f5f9'
                                            }}
                                        >
                                            <img 
                                                src={offerImageUrl}
                                                alt={offer.title}
                                                className="w-100 h-100 object-fit-cover banner-img transition"
                                            />

                                            {/* Top Badges Row */}
                                            <div className="position-absolute top-0 start-0 w-100 p-2.5 p-md-3 d-flex justify-content-between align-items-center" style={{ zIndex: 2 }}>
                                                {discountDisplay && (
                                                    <span 
                                                        className="badge rounded-pill px-2.5 py-1.5 fw-black text-white shadow-sm"
                                                        style={{ 
                                                            fontSize: '12px', 
                                                            background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        {discountDisplay}
                                                    </span>
                                                )}

                                                {offer.valid_to && (
                                                    <div 
                                                        className="badge rounded-pill px-2.5 py-1 text-white d-flex align-items-center gap-1 shadow-sm"
                                                        style={{ 
                                                            backgroundColor: 'rgba(15, 23, 42, 0.72)', 
                                                            backdropFilter: 'blur(8px)', 
                                                            fontSize: '11px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        <Clock size={11} className="text-warning" />
                                                        <span>Expires: {formatDate(offer.valid_to)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Bottom Clean White Card Content */}
                                        <div className="card-body p-3 p-md-3.5 d-flex flex-column justify-content-between">
                                            <div>
                                                <h5 
                                                    onClick={() => navigate(`/offers/${offer.id}`)}
                                                    className="fw-bold text-dark mb-1 cursor-pointer offer-title-link transition text-truncate"
                                                    title={offer.title}
                                                    style={{ fontSize: '16px' }}
                                                >
                                                    {offer.title}
                                                </h5>

                                                <p className="text-muted small mb-3 line-clamp-2" style={{ fontSize: '12.5px', lineHeight: '1.45' }}>
                                                    {stripHtml(offer.description).substring(0, 110) || "Claim this exclusive discount on your next hotel reservation with ZoyoTrip."}
                                                    {stripHtml(offer.description).length > 110 ? '...' : ''}
                                                </p>
                                            </div>

                                            {/* Action Row: Coupon Code + Claim Deal */}
                                            <div className="d-flex align-items-center justify-content-between gap-2 pt-2.5 border-top">
                                                {offer.code ? (
                                                    <div 
                                                        onClick={(e) => handleCopyCode(e, offer.code, offer.id)}
                                                        className="d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5 rounded-3 bg-light border border-primary border-dashed font-monospace shadow-2xs cursor-pointer coupon-pill transition"
                                                        title="Click to copy promo code"
                                                    >
                                                        <Tag size={13} className="text-primary" />
                                                        <span className="fw-black text-dark text-uppercase small" style={{ letterSpacing: '0.5px' }}>
                                                            {offer.code}
                                                        </span>
                                                        <span className="ms-1 border-start ps-1.5 text-primary small d-inline-flex align-items-center gap-0.5" style={{ fontSize: '11px' }}>
                                                            {copiedId === offer.id ? (
                                                                <>
                                                                    <Check size={12} className="text-success" />
                                                                    <span className="text-success fw-bold">Copied!</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy size={12} />
                                                                    <span>Copy</span>
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : <div />}

                                                <button 
                                                    onClick={() => navigate(`/offers/${offer.id}`)}
                                                    className="btn btn-sm btn-primary rounded-pill px-3 py-1.5 fw-bold text-white d-inline-flex align-items-center gap-1 shadow-sm claim-btn transition"
                                                    style={{ fontSize: '12.5px' }}
                                                >
                                                    <span>Claim Deal</span>
                                                    <ArrowRight size={13} />
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Dot Pagination for Carousel */}
                {totalSlides > 1 && (
                    <div className="d-flex justify-content-center align-items-center gap-2 mt-3 mt-md-4">
                        {Array.from({ length: totalSlides }).map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={`carousel-dot transition border-0 rounded-pill p-0 ${
                                    currentIndex === idx ? 'bg-primary active-dot' : 'bg-secondary bg-opacity-25'
                                }`}
                                aria-label={`Go to slide ${idx + 1}`}
                                style={{
                                    width: currentIndex === idx ? '24px' : '7px',
                                    height: '7px',
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .offer-card-clean {
                    border: 1px solid #e2e8f0 !important;
                    transition: transform 0.25s ease, box-shadow 0.25s ease;
                }
                .offer-card-clean:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08) !important;
                }
                .banner-img-wrap:hover .banner-img {
                    transform: scale(1.04);
                }
                .banner-img {
                    transition: transform 0.35s ease;
                }
                .offer-title-link:hover {
                    color: #0d6efd !important;
                }
                .coupon-pill:hover {
                    background-color: #e0edff !important;
                    border-color: #0a58ca !important;
                }
                .claim-btn:hover {
                    transform: scale(1.03);
                }
                .carousel-nav-btn {
                    transition: all 0.2s ease;
                    background-color: #ffffff;
                }
                .carousel-nav-btn:hover {
                    background-color: #0d6efd;
                    color: #ffffff;
                    border-color: #0d6efd !important;
                    transform: scale(1.06);
                }
                .carousel-nav-btn:hover svg {
                    stroke: #ffffff;
                }
                .hover-arrow:hover .transition-arrow {
                    transform: translateX(4px);
                }
                .transition-arrow {
                    transition: transform 0.2s ease;
                }
                .active-dot {
                    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </section>
    );
};

export default SpecialOffers;
